import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Store from '../../../Store/Store';
import { setTotalizerTags } from '../../../Action/Action';
import axiosInstance from '../../../services/axiosConfig';
import {
  Box, Card, CardHeader, CardContent, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Badge, Switch, FormControlLabel,
  TablePagination, InputAdornment, Paper, styled
} from '@mui/material';
import { Plus, Edit2, Trash2, Database, Zap, Activity, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmModal from '../ConfirmModal';
import api from '../../api';
import {
  fetchTags,
  updateTagValue,
  addTag,
  removeTag,
  selectTags,
  selectRealTimeData,
  selectLoading
} from '../../redux/dataSourcesSlice';

// Custom styled switch (same as PLCManagement)
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#ed6c02',
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.5,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
  },
}));

export default function TagManagement({ editTagId, onEditTagIdUsed }) {
  const dispatch = useDispatch();
  const tags = useSelector(selectTags);
  const realTimeData = useSelector(selectRealTimeData);
  const loading = useSelector(selectLoading);
  const totalizerTags = useSelector(state => state.project_reducer?.totalizer_tags || []);
  
  const [channels, setChannels] = useState([]);
  const [tasks, setTasks] = useState([]);  // Alarm task seçimi için
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTagId, setDeleteTagId] = useState(null);

  const [formData, setFormData] = useState({
    tagName: '',
    channelId: '',
    majorAddress: 0,
    minorAddress: 0,
    dbNo: 0,
    type: 0,
    dataType: 0,
    plcId: '',
    moduleId: 0,
    formule: '',
    unit: '',
    alarm: '',
    alarmCondition: null,  // JSON: {operator, value, enabled, taskId}
    alarmTaskId: null,     // Alarm durumunda çalıştırılacak task ID
    dbWriteInterval: 5000  // DB yazma aralığı (ms) - varsayılan 5 saniye
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterType, setFilterType] = useState('');

  // Alarm condition state
  const [alarmEnabled, setAlarmEnabled] = useState(false);
  const [alarmType, setAlarmType] = useState('simple'); // 'simple', 'and', 'or', 'range'
  const [alarmOperator, setAlarmOperator] = useState('>');
  const [alarmValue, setAlarmValue] = useState(0);
  // Bileşik koşullar için
  const [alarmOperator2, setAlarmOperator2] = useState('<');
  const [alarmValue2, setAlarmValue2] = useState(100);
  // Aralık için
  const [rangeMin, setRangeMin] = useState(0);
  const [rangeMax, setRangeMax] = useState(100);
  const [rangeOutside, setRangeOutside] = useState(true); // true: dışında alarm, false: içinde alarm
  // Alarm gecikme süresi (cooldown)
  const [alarmDelaySeconds, setAlarmDelaySeconds] = useState(0);

  // Totalizer state
  const [isTotalizer, setIsTotalizer] = useState(false);

  // SetValue dialog state
  const [writeValueOpen, setWriteValueOpen] = useState(false);
  const [writeValueTagId, setWriteValueTagId] = useState(null);
  const [writeValueTagName, setWriteValueTagName] = useState('');
  const [writeValueInput, setWriteValueInput] = useState('');
  const [writeValueCurrentValue, setWriteValueCurrentValue] = useState(null);

  useEffect(() => {
    dispatch(fetchTags());
    loadChannels();
    loadTasks();
  }, [dispatch]);

  // URL'den gelen editTagId ile modal aç
  useEffect(() => {
    if (editTagId && tags.length > 0) {
      const tagToEdit = tags.find(t => String(t.tagId) === String(editTagId));
      if (tagToEdit) {
        handleOpenDialog(tagToEdit);
        // Kullanıldıktan sonra temizle
        if (onEditTagIdUsed) {
          onEditTagIdUsed();
        }
      }
    }
  }, [editTagId, tags]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTags = async () => {
    try {
      await dispatch(fetchTags()).unwrap();
    } catch (error) {
      console.error('Error loading tags:', error);
      toast.error('Noktalar yüklenirken hata oluştu');
    }
  };

  const loadChannels = async () => {
    try {
      const channelsData = await api.dataSources.getAllChannels();
      const convertedChannels = (channelsData || []).map(channel => ({
        channelId: Number(channel.channelID ?? channel.ChannelID ?? channel.channelId ?? 0),
        channelName: String(channel.channelName ?? channel.ChannelName ?? ''),
        plcId: Number(channel.plcID ?? channel.PlcID ?? channel.plcId ?? 0)
      }));
      setChannels(convertedChannels);
    } catch (error) {
      console.error('Error loading channels:', error);
      setChannels([]);
    }
  };

  const loadTasks = async () => {
    console.log('[loadTasks] Starting to load tasks...');
    console.log('[loadTasks] api object keys:', Object.keys(api));
    console.log('[loadTasks] api.getSavedTasks exists?', typeof api.getSavedTasks);
    try {
      // Task Yönetimi ekranındaki gerçek taskları yükle
      if (typeof api.getSavedTasks !== 'function') {
        console.error('[loadTasks] api.getSavedTasks is not a function!');
        setTasks([]);
        return;
      }
      console.log('[loadTasks] Calling api.getSavedTasks()');
      const tasksData = await api.getSavedTasks();
      console.log('Loaded tasks from API:', tasksData);

      if (!tasksData || tasksData.length === 0) {
        console.warn('No tasks found from API');
        setTasks([]);
        return;
      }

      const convertedTasks = (tasksData || []).map(task => ({
        id: task.name || task.Name || task.taskName,
        name: task.name || task.Name || task.taskName || '',
        description: `${task.type || task.Type || 'workflow'} - ${task.executionMode || task.ExecutionMode || 'scheduler'}`,
        type: task.type || task.Type || 'workflow',
        executionMode: task.executionMode || task.ExecutionMode || 'scheduler',
        isEnabled: task.isEnabled ?? task.IsEnabled ?? true
      }));
      console.log('Converted tasks:', convertedTasks);
      setTasks(convertedTasks);
    } catch (error) {
      console.error('[loadTasks] Error:', error.message, error);
      setTasks([]);
    }
  };

  // Filtered and paginated tags
  const filteredTags = useMemo(() => {
    let result = tags;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(tag =>
        String(tag.tagId).toLowerCase().includes(term) ||
        String(tag.tagName).toLowerCase().includes(term) ||
        String(tag.channelName).toLowerCase().includes(term)
      );
    }

    // Channel filter
    if (filterChannel) {
      result = result.filter(tag => tag.channelId === Number(filterChannel));
    }

    // Type filter
    if (filterType !== '') {
      result = result.filter(tag => tag.type === Number(filterType));
    }

    return result;
  }, [tags, searchTerm, filterChannel, filterType]);

  // Paginated tags
  const paginatedTags = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTags.slice(start, start + rowsPerPage);
  }, [filteredTags, page, rowsPerPage]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterChannel, filterType]);

  // Fetch real-time data only for visible (paginated) tags
  useEffect(() => {
    if (paginatedTags.length > 0) {
      // Initial fetch for current page
      fetchRealTimeDataForPage();

      // Set up interval for periodic updates (every 5 seconds)
      const interval = setInterval(fetchRealTimeDataForPage, 5000);

      return () => {
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedTags]);

  const fetchRealTimeDataForPage = async () => {
    // Fetch real-time data only for visible tags on current page
    if (!paginatedTags || paginatedTags.length === 0) return;

    try {
      const tagIds = paginatedTags.map(tag => tag.tagId).filter(Boolean);

      if (tagIds.length > 0) {
        const tagValues = await api.dataSources.getTagValues(tagIds);

        if (tagValues && Array.isArray(tagValues)) {
          tagValues.forEach(tv => {
            // API returns: tagId (string), tagValue (string with possible whitespace)
            const tagId = tv.tagId ?? tv.TagId ?? tv.TagID;
            const value = (tv.tagValue ?? tv.TagValue ?? tv.value ?? tv.Value ?? '').toString().trim();

            if (tagId !== undefined && tagId !== null) {
              dispatch(updateTagValue({
                tagId: Number(tagId),
                value: value || '—',
                timestamp: new Date().toISOString(),
                quality: 'Good'
              }));
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching real-time tag data:', error);
    }
  };

  const resetAlarmState = () => {
    setAlarmEnabled(false);
    setAlarmType('simple');
    setAlarmOperator('>');
    setAlarmValue(0);
    setAlarmOperator2('<');
    setAlarmValue2(100);
    setRangeMin(0);
    setRangeMax(100);
    setRangeOutside(true);
    setAlarmDelaySeconds(0);
    setIsTotalizer(false);
  };

  // Totalizer toggle handler
  const handleTotalizerToggle = useCallback(async (tagId, currentValue) => {
    if (!tagId) return;

    const newValue = !currentValue;
    setIsTotalizer(newValue);

    try {
      if (newValue) {
        // Add to totalizer
        const companyId = sessionStorage.getItem('TEMPCOM') || localStorage.getItem('temp_com_id') || '1';
        const req = await axiosInstance.post('/tags/AddTagForTotalizer', {
          tagId: String(tagId),
          value: '1',
          companyId: companyId
        });
        if (req.status === 200) {
          Store.dispatch(setTotalizerTags(req.data));
          toast.success('Totalizer olarak işaretlendi');
        }
      } else {
        // Remove from totalizer
        const companyId = sessionStorage.getItem('TEMPCOM') || localStorage.getItem('temp_com_id') || '1';
        const req = await axiosInstance.post('/tags/DeleteTagForTotalizer', {
          tagId: String(tagId),
          companyId: companyId
        });
        if (req.status === 200) {
          Store.dispatch(setTotalizerTags(req.data));
          toast.success('Totalizer işareti kaldırıldı');
        }
      }
    } catch (err) {
      console.error('Totalizer toggle error:', err);
      toast.error('Totalizer değiştirilemedi');
      setIsTotalizer(currentValue); // Revert on error
    }
  }, []);

  const buildAlarmConditionJson = () => {
    if (!alarmEnabled) return null;

    // taskId varsa ekle
    const taskId = formData.alarmTaskId || null;
    // delaySeconds varsa ekle (0'dan büyükse)
    const delaySeconds = alarmDelaySeconds > 0 ? alarmDelaySeconds : undefined;

    if (alarmType === 'simple') {
      return JSON.stringify({
        operator: alarmOperator,
        value: alarmValue,
        enabled: true,
        taskId,
        delaySeconds
      });
    } else if (alarmType === 'and' || alarmType === 'or') {
      return JSON.stringify({
        type: alarmType,
        enabled: true,
        taskId,
        delaySeconds,
        conditions: [
          { operator: alarmOperator, value: alarmValue },
          { operator: alarmOperator2, value: alarmValue2 }
        ]
      });
    } else if (alarmType === 'range') {
      // Aralık: dışında veya içinde alarm
      return JSON.stringify({
        type: rangeOutside ? 'or' : 'and',
        enabled: true,
        taskId,
        delaySeconds,
        conditions: rangeOutside
          ? [
              { operator: '<', value: rangeMin },
              { operator: '>', value: rangeMax }
            ]
          : [
              { operator: '>=', value: rangeMin },
              { operator: '<=', value: rangeMax }
            ]
      });
    }

    return null;
  };

  const handleOpenDialog = (tag = null) => {
    // Dialog açıldığında taskları yükle
    loadTasks();

    if (tag) {
      setSelectedTag(tag);
      // Parse alarmTaskId from alarmCondition if exists
      let parsedTaskId = null;
      if (tag.alarmCondition) {
        try {
          const condition = JSON.parse(tag.alarmCondition);
          parsedTaskId = condition.taskId || null;
        } catch (e) {
          parsedTaskId = null;
        }
      }
      setFormData({
        tagName: tag.tagName,
        channelId: tag.channelId,
        majorAddress: tag.majorAddress,
        minorAddress: tag.minorAddress,
        dbNo: tag.dbNo,
        type: tag.type,
        dataType: tag.dataType,
        plcId: tag.plcId,
        moduleId: tag.moduleId,
        formule: tag.formule,
        unit: tag.unit,
        alarm: tag.alarm,
        alarmCondition: tag.alarmCondition,
        alarmTaskId: parsedTaskId,
        dbWriteInterval: tag.dbWriteInterval || 5000
      });
      // Parse alarm condition if exists
      if (tag.alarmCondition) {
        try {
          const condition = JSON.parse(tag.alarmCondition);
          setAlarmEnabled(condition.enabled || false);
          setAlarmDelaySeconds(condition.delaySeconds || 0);

          if (condition.type === 'and' || condition.type === 'or') {
            setAlarmType(condition.type);
            if (condition.conditions && condition.conditions.length >= 2) {
              setAlarmOperator(condition.conditions[0].operator || '>');
              setAlarmValue(condition.conditions[0].value || 0);
              setAlarmOperator2(condition.conditions[1].operator || '<');
              setAlarmValue2(condition.conditions[1].value || 100);
            }
          } else {
            setAlarmType('simple');
            setAlarmOperator(condition.operator || '>');
            setAlarmValue(condition.value || 0);
          }
        } catch (e) {
          resetAlarmState();
        }
      } else {
        resetAlarmState();
      }
      // Set totalizer state based on totalizerTags
      const isTagTotalizer = totalizerTags.some(t => String(t.tagId) === String(tag.tagId));
      setIsTotalizer(isTagTotalizer);
    } else {
      setSelectedTag(null);
      setFormData({
        tagName: '',
        channelId: '',
        majorAddress: 0,
        minorAddress: 0,
        dbNo: 0,
        type: 0,
        dataType: 0,
        plcId: '',
        moduleId: 0,
        formule: '',
        unit: '',
        alarm: '',
        alarmCondition: null,
        alarmTaskId: null,
        dbWriteInterval: 5000
      });
      resetAlarmState();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTag(null);
  };

  const handleSave = async () => {
    try {
      // Build alarm condition JSON using helper function
      const alarmCondition = buildAlarmConditionJson();

      const dataToSave = {
        ...formData,
        alarmCondition
      };

      if (selectedTag) {
        // Update existing tag
        await api.dataSources.updateTag({ ...selectedTag, ...dataToSave });
        // Re-fetch tags to ensure Redux state matches database
        await dispatch(fetchTags()).unwrap();
        toast.success('Nokta başarıyla güncellendi');
      } else {
        // Create new tag
        const newTagData = await api.dataSources.createTag(dataToSave);
        const newTag = {
          tagId: newTagData.TagID || newTagData.tagId || `TAG_${Date.now()}`,
          ...dataToSave,
          channelName: channels.find(c => c.channelId === formData.channelId)?.channelName || '',
          currentValue: '0',
          quality: 'Unknown',
          lastUpdate: new Date().toLocaleString()
        };
        dispatch(addTag(newTag));
        toast.success('Nokta başarıyla eklendi');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error('Nokta kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const handleDelete = (tagId) => {
    setDeleteTagId(tagId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.dataSources.deleteTag(deleteTagId);
      dispatch(removeTag(deleteTagId));
      toast.success('Nokta başarıyla silindi');
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Nokta silinirken hata oluştu: ' + error.message);
    }
  };

  const handleWriteValue = (tag) => {
    // Get current value from realTimeData or tag
    const realTimeDataEntry = realTimeData[tag.tagId];
    const currentVal = realTimeDataEntry ? realTimeDataEntry.value : tag.currentValue;

    setWriteValueTagId(tag.tagId);
    setWriteValueTagName(tag.tagName || `Tag ${tag.tagId}`);
    setWriteValueCurrentValue(currentVal);
    setWriteValueInput('');
    setWriteValueOpen(true);
  };

  const handleWriteValueClose = () => {
    setWriteValueOpen(false);
    setWriteValueTagId(null);
    setWriteValueTagName('');
    setWriteValueInput('');
    setWriteValueCurrentValue(null);
  };

  const handleWriteValueSubmit = async () => {
    if (!writeValueTagId || writeValueInput === '') {
      toast.warning('Lütfen bir değer girin');
      return;
    }

    try {
      await api.dataSources.writeTagValue(writeValueTagId, writeValueInput);
      dispatch(updateTagValue({
        tagId: writeValueTagId,
        value: writeValueInput,
        timestamp: new Date().toISOString(),
        quality: 'Good'
      }));
      toast.success('Değer başarıyla yazıldı');
      handleWriteValueClose();
    } catch (error) {
      console.error('Error writing value:', error);
      // Fallback to local update
      dispatch(updateTagValue({
        tagId: writeValueTagId,
        value: writeValueInput,
        timestamp: new Date().toISOString(),
        quality: 'Good'
      }));
      toast.success('Değer yazıldı (yerel)');
      handleWriteValueClose();
    }
  };

  const handleWriteValueKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleWriteValueSubmit();
    }
  };

  // BACnet Object Type names
  const getBacnetTypeName = (type) => {
    const types = {
      0: 'AI', 1: 'AO', 2: 'AV',      // Analog Input/Output/Value
      3: 'BI', 4: 'BO', 5: 'BV',      // Binary Input/Output/Value
      13: 'MSI', 14: 'MSO', 19: 'MSV' // Multi-State Input/Output/Value
    };
    return types[type] || `T${type}`;
  };

  const getDataTypeLabel = (type, dataType) => {
    const bacnetType = getBacnetTypeName(type);

    // Determine data type name based on object type category
    let dataTypeName;
    if ([0, 1, 2].includes(type)) { // Analog types
      const types = { 0: 'Bool', 1: 'Int16', 2: 'Float', 3: 'Double', 4: 'Str' };
      dataTypeName = types[dataType] || '?';
    } else if ([3, 4, 5].includes(type)) { // Binary types
      dataTypeName = 'Bool';
    } else if ([13, 14, 19].includes(type)) { // Multi-state types
      dataTypeName = 'UInt32';
    } else {
      dataTypeName = 'Str';
    }

    return `${bacnetType} (${dataTypeName})`;
  };

  const getValueChip = (tag) => {
    // Extract value from realTimeData object or use current value
    const realTimeDataEntry = realTimeData[tag.tagId];
    let rawValue = realTimeDataEntry ? realTimeDataEntry.value : tag.currentValue;

    // Ensure displayValue is always a string, not an object
    let displayValue = '—';
    if (rawValue !== null && rawValue !== undefined) {
      if (typeof rawValue === 'object') {
        displayValue = JSON.stringify(rawValue);
      } else {
        displayValue = String(rawValue);
      }
    }

    const isChanged = realTimeDataEntry && String(realTimeDataEntry.value) !== String(tag.currentValue);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={displayValue}
          size="small"
          color={isChanged ? "warning" : "success"}
          sx={{
            fontFamily: 'monospace',
            minWidth: 60
          }}
        />
        {tag.unit && typeof tag.unit === 'string' && tag.unit.trim() && (
          <Typography variant="caption" color="text.secondary">
            {tag.unit}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Noktalar</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Badge badgeContent={Object.keys(realTimeData).length} color="success">
                  <Activity size={20} />
                </Badge>
                <Chip
                  label={`${tags.length} Nokta`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading || channels.length === 0}
                >
                  Nokta Ekle
                </Button>
              </Box>
            </Box>
          }
        />
        
        <CardContent sx={{ flex: 1, pt: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Search and Filter Toolbar */}
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="ID, Ad veya Kanal ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Kanal Filtrele</InputLabel>
              <Select
                value={filterChannel}
                onChange={(e) => setFilterChannel(e.target.value)}
                label="Kanal Filtrele"
              >
                <MenuItem value="">Tümü</MenuItem>
                {channels.map((channel) => (
                  <MenuItem key={channel.channelId} value={channel.channelId}>
                    {channel.channelName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>BACnet Tipi</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="BACnet Tipi"
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value={0}>AI - Analog Input</MenuItem>
                <MenuItem value={1}>AO - Analog Output</MenuItem>
                <MenuItem value={2}>AV - Analog Value</MenuItem>
                <MenuItem value={3}>BI - Binary Input</MenuItem>
                <MenuItem value={4}>BO - Binary Output</MenuItem>
                <MenuItem value={5}>BV - Binary Value</MenuItem>
                <MenuItem value={13}>MSI - Multi-State Input</MenuItem>
                <MenuItem value={14}>MSO - Multi-State Output</MenuItem>
                <MenuItem value={19}>MSV - Multi-State Value</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            <Typography variant="body2" color="text.secondary">
              {filteredTags.length} / {tags.length} nokta
            </Typography>

            {(searchTerm || filterChannel || filterType !== '') && (
              <Button
                size="small"
                onClick={() => { setSearchTerm(''); setFilterChannel(''); setFilterType(''); }}
              >
                Filtreleri Temizle
              </Button>
            )}
          </Paper>

          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nokta ID</TableCell>
                  <TableCell>Ad</TableCell>
                  <TableCell>Kanal</TableCell>
                  <TableCell>Adres</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Güncel Değer</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTags.map((tag) => (
                  <TableRow key={tag.tagId} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="medium">
                        {tag.tagId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tag.tagName}
                      </Typography>
                      {tag.formule && typeof tag.formule === 'string' && tag.formule.trim() && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Formül: {tag.formule}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tag.channelName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {tag.majorAddress}.{tag.minorAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getDataTypeLabel(tag.type, tag.dataType)} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{getValueChip(tag)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleWriteValue(tag)}
                          color="warning"
                          title="Değer Yaz"
                        >
                          <Zap size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(tag)}
                          color="primary"
                          title="Düzenle"
                        >
                          <Edit2 size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(tag.tagId)}
                          color="error"
                          title="Sil"
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredTags.length === 0 && !loading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              color: 'text.secondary'
            }}>
              <Database size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="body1">
                {tags.length === 0 ? 'Yapılandırılmış nokta yok' : 'Filtreye uygun nokta bulunamadı'}
              </Typography>
              <Typography variant="body2">
                {tags.length === 0
                  ? (channels.length === 0 ? 'Önce kanal ekleyin, sonra nokta oluşturun' : 'Başlamak için "Nokta Ekle" butonuna tıklayın')
                  : 'Farklı filtre kriterleri deneyin'}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredTags.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Sayfa başına:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </CardContent>
      </Card>

      {/* Nokta Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTag ? 'Noktayı Düzenle' : 'Nokta Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Düzenleme modunda ID ve Okunur Adı göster */}
            {selectedTag && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="ID"
                  value={selectedTag.tagId || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: '#f5f5f5' }}
                />
                <TextField
                  label="Okunur Adı"
                  value={selectedTag.tagName2 || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: '#f5f5f5' }}
                />
              </Box>
            )}
            <TextField
              label="Nokta Adı"
              value={formData.tagName}
              onChange={(e) => setFormData({ ...formData, tagName: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Kanal</InputLabel>
              <Select
                value={formData.channelId}
                onChange={(e) => setFormData({ ...formData, channelId: e.target.value })}
                label="Kanal"
              >
                {channels.map((channel) => (
                  <MenuItem key={channel.channelId} value={channel.channelId}>
                    {channel.channelName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ana Adres"
                type="number"
                value={formData.majorAddress}
                onChange={(e) => setFormData({ ...formData, majorAddress: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <TextField
                label="Alt Adres"
                type="number"
                value={formData.minorAddress}
                onChange={(e) => setFormData({ ...formData, minorAddress: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <TextField
                label="DB Numarası"
                type="number"
                value={formData.dbNo}
                onChange={(e) => setFormData({ ...formData, dbNo: parseInt(e.target.value) || 0 })}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>BACnet Nesne Tipi</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="BACnet Nesne Tipi"
                >
                  <MenuItem value={0}>Analog Input (AI) - Salt Okunur</MenuItem>
                  <MenuItem value={1}>Analog Output (AO) - Yazılabilir</MenuItem>
                  <MenuItem value={2}>Analog Value (AV) - Yazılabilir</MenuItem>
                  <MenuItem value={3}>Binary Input (BI) - Salt Okunur</MenuItem>
                  <MenuItem value={4}>Binary Output (BO) - Yazılabilir</MenuItem>
                  <MenuItem value={5}>Binary Value (BV) - Yazılabilir</MenuItem>
                  <MenuItem value={13}>Multi-State Input (MSI)</MenuItem>
                  <MenuItem value={14}>Multi-State Output (MSO)</MenuItem>
                  <MenuItem value={19}>Multi-State Value (MSV)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Veri Tipi</InputLabel>
                <Select
                  value={formData.dataType}
                  onChange={(e) => setFormData({ ...formData, dataType: e.target.value })}
                  label="Veri Tipi"
                >
                  <MenuItem value={0}>Boolean</MenuItem>
                  <MenuItem value={1}>Int16</MenuItem>
                  <MenuItem value={2}>Float</MenuItem>
                  <MenuItem value={3}>Double</MenuItem>
                  <MenuItem value={4}>String</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Formül ve Birim */}
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Değer Dönüşümü</Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label="Wh→kWh (/1000)"
                  size="small"
                  variant="outlined"
                  onClick={() => setFormData({ ...formData, formule: 'x/1000', unit: 'kWh' })}
                />
                <Chip
                  label="W→kW (/1000)"
                  size="small"
                  variant="outlined"
                  onClick={() => setFormData({ ...formData, formule: 'x/1000', unit: 'kW' })}
                />
                <Chip
                  label="mA→A (/1000)"
                  size="small"
                  variant="outlined"
                  onClick={() => setFormData({ ...formData, formule: 'x/1000', unit: 'A' })}
                />
                <Chip
                  label="0-10V→% (*10)"
                  size="small"
                  variant="outlined"
                  onClick={() => setFormData({ ...formData, formule: 'x*10', unit: '%' })}
                />
                <Chip
                  label="0-100→0-1 (/100)"
                  size="small"
                  variant="outlined"
                  onClick={() => setFormData({ ...formData, formule: 'x/100', unit: '' })}
                />
                <Chip
                  label="Sıfırla"
                  size="small"
                  color="default"
                  onClick={() => setFormData({ ...formData, formule: '', unit: '' })}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Formül"
                  value={formData.formule}
                  onChange={(e) => setFormData({ ...formData, formule: e.target.value })}
                  fullWidth
                  placeholder="x/1000 veya x*0.1+5"
                  helperText="x = ham değer. Örnek: x/1000 (Wh→kWh), x*0.1 (ölçekleme)"
                  size="small"
                />
                <TextField
                  label="Birim"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  sx={{ width: 120 }}
                  placeholder="kWh, °C"
                  size="small"
                />
              </Box>
            </Box>

            {/* DB Yazma Aralığı */}
            <FormControl fullWidth>
              <InputLabel>DB Yazma Aralığı</InputLabel>
              <Select
                value={formData.dbWriteInterval}
                onChange={(e) => setFormData({ ...formData, dbWriteInterval: e.target.value })}
                label="DB Yazma Aralığı"
              >
                <MenuItem value={5000}>5 Saniye</MenuItem>
                <MenuItem value={10000}>10 Saniye</MenuItem>
                <MenuItem value={30000}>30 Saniye</MenuItem>
                <MenuItem value={60000}>1 Dakika</MenuItem>
                <MenuItem value={300000}>5 Dakika</MenuItem>
                <MenuItem value={600000}>10 Dakika</MenuItem>
                <MenuItem value={1800000}>30 Dakika</MenuItem>
                <MenuItem value={3600000}>1 Saat</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Tag değeri değiştiğinde veritabanına ne sıklıkla yazılacağı (okuma aralığından bağımsız)
              </Typography>
            </FormControl>

            {/* Totalizer Toggle - Sadece mevcut tag düzenlenirken göster */}
            {selectedTag && (
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
                bgcolor: isTotalizer ? '#e3f2fd' : '#fafafa'
              }}>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={isTotalizer}
                      onChange={() => handleTotalizerToggle(selectedTag.tagId, isTotalizer)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>Totalizer</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sayaç değeri - Raporlamada fark hesabı yapılır (ör: enerji sayacı, su sayacı)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            <TextField
              label="Alarm Mesaji"
              value={formData.alarm}
              onChange={(e) => setFormData({ ...formData, alarm: e.target.value })}
              fullWidth
              placeholder="Yuksek Sicaklik Alarmi"
              helperText="Alarm olusturuldiginda gosterilecek mesaj"
            />

            {/* Otomatik Alarm Kosulu */}
            <Box sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              bgcolor: alarmEnabled ? '#fff3e0' : '#fafafa'
            }}>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={alarmEnabled}
                    onChange={(e) => setAlarmEnabled(e.target.checked)}
                  />
                }
                label="Otomatik Alarm"
              />

              {alarmEnabled && (
                <>
                  {/* Alarm Tipi Secimi */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 2 }}>
                    <Chip
                      label="Tekil"
                      color={alarmType === 'simple' ? 'warning' : 'default'}
                      onClick={() => setAlarmType('simple')}
                      variant={alarmType === 'simple' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      label="VE (AND)"
                      color={alarmType === 'and' ? 'warning' : 'default'}
                      onClick={() => setAlarmType('and')}
                      variant={alarmType === 'and' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      label="VEYA (OR)"
                      color={alarmType === 'or' ? 'warning' : 'default'}
                      onClick={() => setAlarmType('or')}
                      variant={alarmType === 'or' ? 'filled' : 'outlined'}
                    />
                    <Chip
                      label="Aralik"
                      color={alarmType === 'range' ? 'warning' : 'default'}
                      onClick={() => setAlarmType('range')}
                      variant={alarmType === 'range' ? 'filled' : 'outlined'}
                    />
                  </Box>

                  {/* Tekil Kosul */}
                  {alarmType === 'simple' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography>Tag degeri</Typography>
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          value={alarmOperator}
                          onChange={(e) => setAlarmOperator(e.target.value)}
                        >
                          <MenuItem value=">">{'>'}</MenuItem>
                          <MenuItem value="<">{'<'}</MenuItem>
                          <MenuItem value="=">=</MenuItem>
                          <MenuItem value=">=">{'>='}</MenuItem>
                          <MenuItem value="<=">{'<='}</MenuItem>
                          <MenuItem value="!=">{'!='}</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        type="number"
                        size="small"
                        value={alarmValue}
                        onChange={(e) => setAlarmValue(parseFloat(e.target.value) || 0)}
                        sx={{ width: 120 }}
                      />
                      <Typography color="text.secondary">ise alarm</Typography>
                    </Box>
                  )}

                  {/* Bilesik Kosul (AND/OR) */}
                  {(alarmType === 'and' || alarmType === 'or') && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography>Kosul 1:</Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={alarmOperator}
                            onChange={(e) => setAlarmOperator(e.target.value)}
                          >
                            <MenuItem value=">">{'>'}</MenuItem>
                            <MenuItem value="<">{'<'}</MenuItem>
                            <MenuItem value="=">=</MenuItem>
                            <MenuItem value=">=">{'>='}</MenuItem>
                            <MenuItem value="<=">{'<='}</MenuItem>
                            <MenuItem value="!=">{'!='}</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          type="number"
                          size="small"
                          value={alarmValue}
                          onChange={(e) => setAlarmValue(parseFloat(e.target.value) || 0)}
                          sx={{ width: 120 }}
                        />
                      </Box>
                      <Typography color="warning.main" fontWeight="bold" sx={{ textAlign: 'center' }}>
                        {alarmType === 'and' ? 'VE' : 'VEYA'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography>Kosul 2:</Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={alarmOperator2}
                            onChange={(e) => setAlarmOperator2(e.target.value)}
                          >
                            <MenuItem value=">">{'>'}</MenuItem>
                            <MenuItem value="<">{'<'}</MenuItem>
                            <MenuItem value="=">=</MenuItem>
                            <MenuItem value=">=">{'>='}</MenuItem>
                            <MenuItem value="<=">{'<='}</MenuItem>
                            <MenuItem value="!=">{'!='}</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          type="number"
                          size="small"
                          value={alarmValue2}
                          onChange={(e) => setAlarmValue2(parseFloat(e.target.value) || 0)}
                          sx={{ width: 120 }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Aralik Kosulu */}
                  {alarmType === 'range' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography>Min:</Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={rangeMin}
                          onChange={(e) => setRangeMin(parseFloat(e.target.value) || 0)}
                          sx={{ width: 120 }}
                        />
                        <Typography>Max:</Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={rangeMax}
                          onChange={(e) => setRangeMax(parseFloat(e.target.value) || 0)}
                          sx={{ width: 120 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label="Aralik disinda alarm"
                          color={rangeOutside ? 'error' : 'default'}
                          onClick={() => setRangeOutside(true)}
                          variant={rangeOutside ? 'filled' : 'outlined'}
                          size="small"
                        />
                        <Chip
                          label="Aralik icinde alarm"
                          color={!rangeOutside ? 'error' : 'default'}
                          onClick={() => setRangeOutside(false)}
                          variant={!rangeOutside ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {rangeOutside
                          ? `Deger ${rangeMin}'den kucuk VEYA ${rangeMax}'den buyukse alarm`
                          : `Deger ${rangeMin} ile ${rangeMax} arasinda ise alarm`
                        }
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {alarmEnabled && (
                <>
                  {/* Alarm Task Seçimi */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Alarm Görevi (Opsiyonel)</InputLabel>
                      <Select
                        value={formData.alarmTaskId || ''}
                        onChange={(e) => setFormData({ ...formData, alarmTaskId: e.target.value || null })}
                        label="Alarm Görevi (Opsiyonel)"
                      >
                        <MenuItem value="">
                          <em>Görev Yok - Sadece Alarm Oluştur</em>
                        </MenuItem>
                        {tasks.map((task) => (
                          <MenuItem key={String(task.id || '')} value={String(task.id || '')}>
                            <Box>
                              <Typography variant="body2">{String(task.name || '')}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {String(task.description || '')}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {formData.alarmTaskId && (
                      <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                        Alarm oluştuğunda "{String(tasks.find(t => t.id === formData.alarmTaskId)?.name || '')}" görevi çalıştırılacak.
                      </Typography>
                    )}
                  </Box>

                  {/* Alarm Gecikme Süresi (Cooldown) */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Yeniden Tetikleme Gecikmesi</InputLabel>
                      <Select
                        value={alarmDelaySeconds}
                        onChange={(e) => setAlarmDelaySeconds(Number(e.target.value))}
                        label="Yeniden Tetikleme Gecikmesi"
                      >
                        <MenuItem value={0}>Gecikme Yok</MenuItem>
                        <MenuItem value={60}>1 Dakika</MenuItem>
                        <MenuItem value={300}>5 Dakika</MenuItem>
                        <MenuItem value={600}>10 Dakika</MenuItem>
                        <MenuItem value={900}>15 Dakika</MenuItem>
                        <MenuItem value={1800}>30 Dakika</MenuItem>
                        <MenuItem value={3600}>1 Saat</MenuItem>
                        <MenuItem value={7200}>2 Saat</MenuItem>
                        <MenuItem value={14400}>4 Saat</MenuItem>
                        <MenuItem value={28800}>8 Saat</MenuItem>
                        <MenuItem value={86400}>24 Saat</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Alarm kapandıktan sonra bu süre geçmeden tekrar tetiklenmez.
                      Örn: Sıcaklık 54.9°C → alarm, 55.1°C → alarm kapanır, 54.8°C → gecikme süresince tekrar alarm oluşmaz.
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Kosul saglandiginda otomatik alarm olusturulur, saglanmadiginda kapanir.
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.tagName || !formData.channelId}
          >
            {selectedTag ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SetValue Dialog */}
      <Dialog
        open={writeValueOpen}
        onClose={handleWriteValueClose}
        maxWidth="xs"
        fullWidth
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
          }
        }}
        PaperProps={{
          sx: { backgroundColor: '#f5f5f5' }
        }}
      >
        <DialogTitle>
          Değer Yaz
          <Typography variant="body2" color="text.secondary">
            {writeValueTagName} (ID: {writeValueTagId})
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {writeValueCurrentValue !== null && writeValueCurrentValue !== undefined && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mevcut Değer
                </Typography>
                <Typography variant="h6" color="primary">
                  {writeValueCurrentValue}
                </Typography>
              </Box>
            )}

            <TextField
              autoFocus
              fullWidth
              label="Yeni Değer"
              value={writeValueInput}
              onChange={(e) => setWriteValueInput(e.target.value)}
              onKeyPress={handleWriteValueKeyPress}
              placeholder="Değer girin..."
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWriteValueClose}>
            İptal
          </Button>
          <Button
            onClick={handleWriteValueSubmit}
            variant="contained"
            color="warning"
            disabled={writeValueInput === ''}
            startIcon={<Zap size={16} />}
          >
            Yaz
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Noktayı Sil"
        content="Bu noktayı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        type="yesNo"
      />
    </Box>
  );
}